import {Fragment, useEffect, useMemo, useState} from 'react';
import {Dialog, Transition} from '@headlessui/react';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {AlertTriangle, CheckCircle2, FlaskConical, Search, X} from 'lucide-react';
import clsx from 'clsx';

import AdminService from '@/modules/admin/services/adminService';
import {useOhdMetrics} from '@/modules/ohd/hooks/useOhdMetrics';

interface Props {
    open: boolean;
    onClose: () => void;
    /** MLD organization id (passed to BE for the lazy-sync path). */
    orgId: number | null;
    /** OHD organization id (numeric, from tenant features). Drives the
     * `useOhdMetrics` cache lookup so this modal reuses whatever the
     * dashboard already loaded. */
    ohdOrgId: number | null;
    /** Optional default campaign to focus on open. */
    defaultCampaignId?: number;
}

type AssayOption = {
    code: string;
    name: string;
    isBundledPanel: boolean;
    isCalculated: boolean;
    classification: number | null;
};

type PanelTest = {
    id: number;
    name: string;
    loinc: string | null;
    occurrenceCount: number;
    lastSeenAt: string;
};

type PanelRow = {
    id: number;
    campaignId: number;
    panel: string;
    assayCode: string | null;
    showAsCard: boolean;
    displayName: string | null;
    sortOrder: number;
    genderFilter: 'm' | 'f' | null;
    placeholder: boolean;
    loincOverrides: string[] | null;
    tests: PanelTest[];
};

type CampaignOption = {id: number; name: string; startsAt: string | null; endsAt: string | null};

/**
 * Super-admin curation for OHD panels — per-(campaign, panel).
 *
 * Top: campaign picker (which campaign are we curating?).
 * Left pane: panels in the selected campaign with on/off toggles.
 * Right pane: panel detail — display/sort/gender overrides, the
 * "Map panel to assay" picker, and an informational list of the OHD
 * tests Sean returned under this panel.
 *
 * No per-test curation: every test contributes to the panel's count
 * implicitly through the bound assay's elis_panel_code. Tests with
 * no panel string surface under an "Unassigned" group.
 */
export default function OhdLabelMapping({open, onClose, orgId, ohdOrgId, defaultCampaignId}: Props) {
    const queryClient = useQueryClient();
    const [selectedCampaignId, setSelectedCampaignId] = useState<number | null>(defaultCampaignId ?? null);
    const [selectedPanelId, setSelectedPanelId] = useState<number | null>(null);
    const [unassignedSelected, setUnassignedSelected] = useState(false);
    const [assaySearch, setAssaySearch] = useState('');

    // Reset when modal closes / orgId changes.
    useEffect(() => {
        if (!open) {
            setSelectedCampaignId(defaultCampaignId ?? null);
            setSelectedPanelId(null);
            setUnassignedSelected(false);
            setAssaySearch('');
        }
    }, [open, defaultCampaignId]);

    // 1) Campaign list — reuses the dashboard's `useOhdMetrics` cache
    // entry. If the OHD Organization Dashboard rendered first (the
    // common path), this returns instantly from cache. If the modal
    // is opened cold, this fires the same metrics fetch the dashboard
    // would do, populating both caches at once.
    const metricsQuery = useOhdMetrics(ohdOrgId ?? undefined);
    const campaigns: CampaignOption[] = useMemo(() => {
        const list = metricsQuery.data?.campaign_list ?? [];
        return list.map((c) => ({
            id: c.id,
            name: c.name,
            startsAt: c.starts_at,
            endsAt: c.ends_at,
        }));
    }, [metricsQuery.data]);

    // Default the campaign picker to the most-recent campaign (last in
    // the array, OHD returns ordered by start_at desc usually — but we
    // sort here defensively) once campaigns load.
    useEffect(() => {
        if (selectedCampaignId !== null) return;
        if (campaigns.length === 0) return;
        const sorted = [...campaigns].sort((a, b) => (b.startsAt ?? '').localeCompare(a.startsAt ?? ''));
        setSelectedCampaignId(sorted[0].id);
    }, [campaigns, selectedCampaignId]);

    // 2) Panel curation for the selected campaign. Pass orgId so the
    // BE's lazy-sync path can fire on first sighting of a campaign.
    const curationQuery = useQuery({
        queryKey: ['ohd-campaign-panel-curation', selectedCampaignId, orgId],
        queryFn: () => AdminService.getOhdCampaignPanels(selectedCampaignId!, orgId),
        enabled: open && !!selectedCampaignId,
        staleTime: 30_000,
    });

    const panels = useMemo<PanelRow[]>(() => curationQuery.data?.panels ?? [], [curationQuery.data]);
    const unassignedTests = useMemo<PanelTest[]>(
        () => curationQuery.data?.unassignedTests ?? [],
        [curationQuery.data]
    );
    const assays = useMemo<AssayOption[]>(() => curationQuery.data?.assays ?? [], [curationQuery.data]);

    // Default the panel selection to the first panel (or unassigned bucket)
    // when curation loads for a campaign.
    useEffect(() => {
        if (selectedPanelId !== null || unassignedSelected) return;
        if (panels.length > 0) {
            const firstOn = panels.find((p) => p.showAsCard);
            setSelectedPanelId((firstOn ?? panels[0]).id);
        } else if (unassignedTests.length > 0) {
            setUnassignedSelected(true);
        }
    }, [panels, unassignedTests, selectedPanelId, unassignedSelected]);

    // Reset panel selection when campaign changes (different panel set).
    useEffect(() => {
        setSelectedPanelId(null);
        setUnassignedSelected(false);
        setAssaySearch('');
    }, [selectedCampaignId]);

    const selectedPanel = useMemo(
        () => panels.find((p) => p.id === selectedPanelId) ?? null,
        [panels, selectedPanelId]
    );

    const invalidate = () => {
        // Catch every variant of the curation query key (orgId may or
        // may not have been included). Same for the cards key.
        void queryClient.invalidateQueries({queryKey: ['ohd-campaign-panel-curation']});
        void queryClient.invalidateQueries({queryKey: ['smart-testing-cards']});
    };

    const toggleMutation = useMutation({
        mutationFn: (vars: {mappingId: number; showAsCard: boolean}) =>
            AdminService.updateOhdCampaignPanel(vars.mappingId, {showAsCard: vars.showAsCard}),
        onSuccess: invalidate,
    });

    const updateMutation = useMutation({
        mutationFn: (vars: {
            mappingId: number;
            updates: Parameters<typeof AdminService.updateOhdCampaignPanel>[1];
        }) => AdminService.updateOhdCampaignPanel(vars.mappingId, vars.updates),
        onSuccess: invalidate,
    });

    const anyPending = toggleMutation.isPending || updateMutation.isPending;

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={anyPending ? () => {} : onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-200"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-150"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0">
                    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-start justify-center px-4 py-10">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-200"
                            enterFrom="opacity-0 translate-y-2"
                            enterTo="opacity-100 translate-y-0"
                            leave="ease-in duration-150"
                            leaveFrom="opacity-100 translate-y-0"
                            leaveTo="opacity-0 translate-y-2">
                            <Dialog.Panel className="w-full max-w-6xl rounded-xl border border-mld-panel-border bg-white shadow-xl">
                                {/* Header */}
                                <div className="flex items-start justify-between gap-4 border-b border-mld-panel-border px-5 py-3">
                                    <div className="min-w-0 flex-1">
                                        <Dialog.Title className="text-sm font-semibold text-mld-menu-text">
                                            OHD Panel Curation
                                        </Dialog.Title>
                                        <p className="mt-0.5 text-[11px] text-mld-muted-text">
                                            Decide which OHD panels render as Smart Testing cards on the org dashboard
                                            for the selected campaign, and which assay each panel binds to. New panels
                                            OHD adds default to OFF.
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-mld-muted-text">
                                            Campaign
                                        </label>
                                        <select
                                            disabled={metricsQuery.isLoading || campaigns.length === 0 || anyPending}
                                            value={selectedCampaignId ?? ''}
                                            onChange={(e) => setSelectedCampaignId(Number(e.target.value) || null)}
                                            className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-mld-menu-text focus:border-mld-primary focus:outline-none focus:ring-1 focus:ring-mld-primary disabled:opacity-50">
                                            {campaigns.length === 0 && <option value="">No campaigns</option>}
                                            {campaigns.map((c) => (
                                                <option key={c.id} value={c.id}>
                                                    {c.name}
                                                </option>
                                            ))}
                                        </select>
                                        <button
                                            type="button"
                                            onClick={onClose}
                                            disabled={anyPending}
                                            className="rounded p-1 text-mld-muted-text hover:bg-mld-menu-background hover:text-mld-text-secondary disabled:opacity-50">
                                            <X className="h-4 w-4" />
                                        </button>
                                    </div>
                                </div>

                                {/* Body */}
                                <div className="px-5 py-4">
                                    {!orgId && (
                                        <div className="rounded-md border border-mld-warning/30 bg-mld-warning/5 p-3 text-xs text-mld-warning">
                                            No organization context. Open this modal from an OHD-enabled org dashboard.
                                        </div>
                                    )}
                                    {orgId && metricsQuery.isLoading && (
                                        <p className="text-xs text-mld-muted-text">Loading campaigns…</p>
                                    )}
                                    {orgId && metricsQuery.isError && (
                                        <div className="rounded-md border border-mld-danger/20 bg-mld-danger/5 p-3 text-xs text-mld-danger">
                                            Failed to load campaigns:{' '}
                                            {metricsQuery.error instanceof Error
                                                ? metricsQuery.error.message
                                                : 'Unknown error'}
                                        </div>
                                    )}
                                    {orgId && !metricsQuery.isLoading && campaigns.length === 0 && (
                                        <div className="flex items-center gap-2 rounded-md border border-mld-warning/30 bg-mld-warning/5 p-3 text-xs text-mld-warning">
                                            <AlertTriangle className="h-4 w-4" />
                                            This organization has no OHD campaigns. Confirm
                                            <span className="font-mono">features.ohd_organization_id</span> is set.
                                        </div>
                                    )}
                                    {selectedCampaignId && curationQuery.isLoading && (
                                        <p className="text-xs text-mld-muted-text">Loading panels…</p>
                                    )}
                                    {selectedCampaignId && curationQuery.isError && (
                                        <div className="rounded-md border border-mld-danger/20 bg-mld-danger/5 p-3 text-xs text-mld-danger">
                                            Failed to load panel curation:{' '}
                                            {curationQuery.error instanceof Error
                                                ? curationQuery.error.message
                                                : 'Unknown error'}
                                        </div>
                                    )}
                                    {selectedCampaignId &&
                                        !curationQuery.isLoading &&
                                        !curationQuery.isError &&
                                        panels.length === 0 &&
                                        unassignedTests.length === 0 && (
                                            <div className="flex items-center gap-2 rounded-md border border-mld-success/20 bg-mld-success/5 p-4 text-sm text-mld-success">
                                                <CheckCircle2 className="h-4 w-4" />
                                                No panels imported for this campaign yet. Run an OHD sync to populate.
                                            </div>
                                        )}

                                    {selectedCampaignId &&
                                        !curationQuery.isLoading &&
                                        !curationQuery.isError &&
                                        (panels.length > 0 || unassignedTests.length > 0) && (
                                            <div className="grid grid-cols-12 gap-4">
                                                <PanelList
                                                    panels={panels}
                                                    unassignedTests={unassignedTests}
                                                    selectedPanelId={selectedPanelId}
                                                    unassignedSelected={unassignedSelected}
                                                    onSelectPanel={(id) => {
                                                        updateMutation.reset();
                                                        setUnassignedSelected(false);
                                                        setSelectedPanelId(id);
                                                    }}
                                                    onSelectUnassigned={() => {
                                                        updateMutation.reset();
                                                        setSelectedPanelId(null);
                                                        setUnassignedSelected(true);
                                                    }}
                                                />
                                                <section className="col-span-8 rounded-md border border-mld-panel-border bg-white">
                                                    {unassignedSelected ? (
                                                        <UnassignedDetail tests={unassignedTests} />
                                                    ) : selectedPanel ? (
                                                        <PanelDetail
                                                            panel={selectedPanel}
                                                            assays={assays}
                                                            assaySearch={assaySearch}
                                                            setAssaySearch={setAssaySearch}
                                                            onToggle={(showAsCard) =>
                                                                toggleMutation.mutate({
                                                                    mappingId: selectedPanel.id,
                                                                    showAsCard,
                                                                })
                                                            }
                                                            onUpdate={(updates) =>
                                                                updateMutation.mutate({
                                                                    mappingId: selectedPanel.id,
                                                                    updates,
                                                                })
                                                            }
                                                            toggleBusy={toggleMutation.isPending}
                                                            updateBusy={updateMutation.isPending}
                                                            updateError={
                                                                updateMutation.error instanceof Error
                                                                    ? updateMutation.error.message
                                                                    : null
                                                            }
                                                        />
                                                    ) : (
                                                        <div className="flex h-full items-center justify-center px-6 py-10 text-xs text-mld-muted-text">
                                                            Select a panel on the left to begin.
                                                        </div>
                                                    )}
                                                </section>
                                            </div>
                                        )}
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
}

// ── Sub-components ────────────────────────────────────────────────────

function PanelList({
    panels,
    unassignedTests,
    selectedPanelId,
    unassignedSelected,
    onSelectPanel,
    onSelectUnassigned,
}: {
    panels: PanelRow[];
    unassignedTests: PanelTest[];
    selectedPanelId: number | null;
    unassignedSelected: boolean;
    onSelectPanel: (id: number) => void;
    onSelectUnassigned: () => void;
}) {
    return (
        <aside className="col-span-4 max-h-[65vh] overflow-y-auto rounded-md border border-mld-panel-border bg-white">
            <div className="sticky top-0 border-b border-mld-panel-border bg-[#f8f6f1] px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-mld-muted-text">
                Panels ({panels.length})
            </div>
            <ul className="divide-y divide-[#efeae2]">
                {panels.map((panel) => {
                    const isSelected = panel.id === selectedPanelId && !unassignedSelected;
                    return (
                        <li key={panel.id}>
                            <button
                                type="button"
                                onClick={() => onSelectPanel(panel.id)}
                                className={clsx(
                                    'flex w-full items-start gap-2 px-3 py-2.5 text-left transition',
                                    isSelected
                                        ? 'bg-mld-primary/10 text-mld-primary'
                                        : 'text-mld-text-secondary hover:bg-[#f4f1eb]'
                                )}>
                                <PanelStatusDot panel={panel} />
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2">
                                        <span className="truncate text-xs font-semibold leading-snug">
                                            {panel.displayName ?? panel.panel}
                                        </span>
                                        <PanelOnOffBadge panel={panel} />
                                        <PanelAwaitingBadge panel={panel} />
                                    </div>
                                    <div className="mt-0.5 text-[11px] text-mld-muted-text">
                                        {panel.tests.length} test
                                        {panel.tests.length === 1 ? '' : 's'}
                                        {panel.assayCode && (
                                            <>
                                                {' · '}
                                                <span className="font-mono">{panel.assayCode}</span>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </button>
                        </li>
                    );
                })}
                {unassignedTests.length > 0 && (
                    <li>
                        <button
                            type="button"
                            onClick={onSelectUnassigned}
                            className={clsx(
                                'flex w-full items-start gap-2 px-3 py-2.5 text-left transition',
                                unassignedSelected
                                    ? 'bg-mld-primary/10 text-mld-primary'
                                    : 'text-mld-text-secondary hover:bg-[#f4f1eb]'
                            )}>
                            <span className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full bg-mld-muted-text/40" />
                            <div className="min-w-0 flex-1">
                                <span className="text-xs font-semibold leading-snug">Unassigned</span>
                                <div className="mt-0.5 text-[11px] text-mld-muted-text">
                                    {unassignedTests.length} test
                                    {unassignedTests.length === 1 ? '' : 's'} with no panel
                                </div>
                            </div>
                        </button>
                    </li>
                )}
            </ul>
        </aside>
    );
}

function PanelStatusDot({panel}: {panel: PanelRow}) {
    const color = panel.placeholder
        ? 'bg-mld-warning/80'
        : panel.showAsCard
          ? 'bg-mld-status-normal'
          : 'bg-mld-muted-text/40';
    return <span className={clsx('mt-1.5 h-2 w-2 flex-shrink-0 rounded-full', color)} />;
}

function PanelOnOffBadge({panel}: {panel: PanelRow}) {
    return (
        <span
            className={clsx(
                'rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide',
                panel.showAsCard ? 'bg-mld-success/10 text-mld-success' : 'bg-mld-menu-background text-mld-text-secondary'
            )}>
            {panel.showAsCard ? 'On' : 'Off'}
        </span>
    );
}

function PanelAwaitingBadge({panel}: {panel: PanelRow}) {
    if (!panel.placeholder) return null;
    return (
        <span className="rounded-full bg-mld-warning/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-mld-warning">
            Awaiting
        </span>
    );
}

function PanelDetail({
    panel,
    assays,
    assaySearch,
    setAssaySearch,
    onToggle,
    onUpdate,
    toggleBusy,
    updateBusy,
    updateError,
}: {
    panel: PanelRow;
    assays: AssayOption[];
    assaySearch: string;
    setAssaySearch: (v: string) => void;
    onToggle: (showAsCard: boolean) => void;
    onUpdate: (updates: Parameters<typeof AdminService.updateOhdCampaignPanel>[1]) => void;
    toggleBusy: boolean;
    updateBusy: boolean;
    updateError: string | null;
}) {
    const [displayName, setDisplayName] = useState(panel.displayName ?? '');
    const [sortOrder, setSortOrder] = useState(String(panel.sortOrder));
    const [loincOverrides, setLoincOverrides] = useState((panel.loincOverrides ?? []).join(', '));

    useEffect(() => {
        setDisplayName(panel.displayName ?? '');
        setSortOrder(String(panel.sortOrder));
        setLoincOverrides((panel.loincOverrides ?? []).join(', '));
    }, [panel.id, panel.displayName, panel.sortOrder, panel.loincOverrides]);

    const commitDisplayName = () => {
        const trimmed = displayName.trim();
        const current = panel.displayName ?? '';
        if (trimmed === current) return;
        onUpdate({displayName: trimmed === '' ? null : trimmed});
    };

    const commitSortOrder = () => {
        const parsed = Number(sortOrder);
        if (!Number.isFinite(parsed) || Math.floor(parsed) === panel.sortOrder) return;
        onUpdate({sortOrder: Math.floor(parsed)});
    };

    const commitLoincOverrides = () => {
        const trimmed = loincOverrides.trim();
        const current = (panel.loincOverrides ?? []).join(', ');
        if (trimmed === current) return;
        // Raw string goes to the BE, which parses, validates each code,
        // and 422s naming any offending value — surfaced verbatim below.
        onUpdate({loincOverrides: trimmed === '' ? null : trimmed});
    };

    return (
        <div className="flex h-full flex-col">
            {/* Header */}
            <header className="border-b border-mld-panel-border bg-[#fbfaf6] px-4 py-3">
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-mld-muted-text">
                            OHD panel
                        </div>
                        <div className="mt-0.5 flex items-center gap-2">
                            <FlaskConical className="h-4 w-4 text-mld-primary" />
                            <span className="text-sm font-semibold text-mld-menu-text">{panel.panel}</span>
                            <PanelOnOffBadge panel={panel} />
                            <PanelAwaitingBadge panel={panel} />
                        </div>
                        <div className="mt-1 text-[11px] text-mld-muted-text">
                            {panel.tests.length} test{panel.tests.length === 1 ? '' : 's'} from OHD
                            {panel.assayCode && (
                                <>
                                    {' · '}mapped to <span className="font-mono">{panel.assayCode}</span>
                                </>
                            )}
                        </div>
                    </div>
                    <button
                        type="button"
                        disabled={toggleBusy}
                        onClick={() => onToggle(!panel.showAsCard)}
                        className={clsx(
                            'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition disabled:opacity-50',
                            panel.showAsCard
                                ? 'bg-mld-success text-white hover:bg-mld-success/90'
                                : 'bg-mld-panel-border text-mld-text-secondary hover:bg-mld-muted-text/40'
                        )}>
                        {panel.showAsCard ? 'Showing as card' : 'Hidden'}
                    </button>
                </div>

                <div className="mt-3 grid grid-cols-3 gap-3">
                    <label className="block">
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-mld-muted-text">
                            Display name
                        </span>
                        <input
                            type="text"
                            value={displayName}
                            placeholder={panel.panel}
                            onChange={(e) => setDisplayName(e.target.value)}
                            onBlur={commitDisplayName}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                            }}
                            disabled={updateBusy}
                            className="mt-1 w-full rounded-md border border-mld-panel-border bg-white px-2 py-1 text-xs text-mld-menu-text placeholder:text-mld-muted-text focus:border-mld-primary focus:outline-none focus:ring-1 focus:ring-mld-primary"
                        />
                    </label>
                    <label className="block">
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-mld-muted-text">
                            Sort order
                        </span>
                        <input
                            type="number"
                            value={sortOrder}
                            onChange={(e) => setSortOrder(e.target.value)}
                            onBlur={commitSortOrder}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                            }}
                            disabled={updateBusy}
                            className="mt-1 w-full rounded-md border border-mld-panel-border bg-white px-2 py-1 text-xs text-mld-menu-text focus:border-mld-primary focus:outline-none focus:ring-1 focus:ring-mld-primary"
                        />
                    </label>
                    <label className="block">
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-mld-muted-text">
                            Gender filter
                        </span>
                        <select
                            value={panel.genderFilter ?? ''}
                            onChange={(e) => {
                                const v = e.target.value;
                                onUpdate({genderFilter: v === '' ? null : (v as 'm' | 'f')});
                            }}
                            disabled={updateBusy}
                            className="mt-1 w-full rounded-md border border-mld-panel-border bg-white px-2 py-1 text-xs text-mld-menu-text focus:border-mld-primary focus:outline-none focus:ring-1 focus:ring-mld-primary">
                            <option value="">Everyone</option>
                            <option value="m">Males only</option>
                            <option value="f">Females only</option>
                        </select>
                    </label>
                </div>

                <div className="mt-3 grid grid-cols-3 gap-3">
                    <label className="col-span-2 block">
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-mld-muted-text">
                            LOINC overrides (OHD mode)
                        </span>
                        <input
                            type="text"
                            value={loincOverrides}
                            placeholder="e.g. 61128-5, 10365-5"
                            onChange={(e) => setLoincOverrides(e.target.value)}
                            onBlur={commitLoincOverrides}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                            }}
                            disabled={updateBusy}
                            className="mt-1 w-full rounded-md border border-mld-panel-border bg-white px-2 py-1 font-mono text-xs text-mld-menu-text placeholder:text-mld-muted-text focus:border-mld-primary focus:outline-none focus:ring-1 focus:ring-mld-primary"
                        />
                        <span className="mt-1 block text-[10px] text-mld-muted-text">
                            Comma-separated. When set, the card counts exactly these LOINCs instead of OHD&apos;s
                            panel grouping. Leave empty to use the panel grouping.
                        </span>
                    </label>
                    <label className="flex cursor-pointer items-start gap-2 pt-5">
                        <input
                            type="checkbox"
                            checked={panel.placeholder}
                            disabled={updateBusy}
                            onChange={(e) => onUpdate({placeholder: e.target.checked})}
                            className="mt-0.5 h-3.5 w-3.5 rounded text-mld-primary focus:ring-mld-primary"
                        />
                        <span className="text-xs font-semibold text-mld-text-secondary">
                            Awaiting results
                            <span className="block text-[10px] font-normal text-mld-muted-text">
                                Placeholder — completion count stays 0
                            </span>
                        </span>
                    </label>
                </div>

                {updateError && (
                    <div className="mt-2 rounded-md border border-mld-danger/20 bg-mld-danger/5 px-3 py-2 text-[11px] text-mld-danger">
                        {updateError}
                    </div>
                )}
            </header>

            <div className="grid min-h-[280px] flex-1 grid-cols-2 divide-x divide-mld-panel-border">
                {/* Left: bundled-panel radios (top) + tests-in-this-panel list (bottom) */}
                <div className="flex flex-col">
                    <BundledPanelRadios
                        assays={assays}
                        selectedCode={panel.assayCode}
                        disabled={updateBusy}
                        onChange={(code) => onUpdate({assayCode: code})}
                    />
                    <div className="border-y border-mld-panel-border bg-[#f8f6f1] px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-mld-muted-text">
                        Tests in this panel ({panel.tests.length})
                    </div>
                    <ul className="max-h-[35vh] overflow-y-auto divide-y divide-[#efeae2]">
                        {panel.tests.length === 0 && (
                            <li className="px-3 py-6 text-center text-[11px] text-mld-muted-text">
                                No tests under this panel in this campaign.
                            </li>
                        )}
                        {panel.tests.map((t) => (
                            <li key={t.id} className="px-3 py-2 text-xs">
                                <div className="font-semibold text-mld-menu-text">{t.name}</div>
                                <div className="text-[11px] text-mld-muted-text">
                                    {t.loinc ? `LOINC ${t.loinc}` : 'no LOINC'} · seen {t.occurrenceCount}×
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Right: individual-assay search picker (bundled rows excluded — they live on the left) */}
                <IndividualAssayPicker
                    assays={assays}
                    assaySearch={assaySearch}
                    setAssaySearch={setAssaySearch}
                    selectedCode={panel.assayCode}
                    disabled={updateBusy}
                    onChange={(code) => onUpdate({assayCode: code})}
                />
            </div>
        </div>
    );
}

function UnassignedDetail({tests}: {tests: PanelTest[]}) {
    return (
        <div className="flex h-full flex-col">
            <header className="border-b border-mld-panel-border bg-[#fbfaf6] px-4 py-3">
                <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-mld-muted-text">
                    Unassigned tests
                </div>
                <p className="mt-0.5 text-[11px] text-mld-muted-text">
                    Tests OHD returned without a panel string. Informational only — these don&apos;t feed any card.
                </p>
            </header>
            <ul className="max-h-[55vh] overflow-y-auto divide-y divide-[#efeae2]">
                {tests.map((t) => (
                    <li key={t.id} className="px-3 py-2 text-xs">
                        <div className="font-semibold text-mld-menu-text">{t.name}</div>
                        <div className="text-[11px] text-mld-muted-text">
                            {t.loinc ? `LOINC ${t.loinc}` : 'no LOINC'} · seen {t.occurrenceCount}×
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
}

function BundledPanelRadios({
    assays,
    selectedCode,
    disabled,
    onChange,
}: {
    assays: AssayOption[];
    selectedCode: string | null;
    disabled: boolean;
    onChange: (code: string | null) => void;
}) {
    // The "always available" Level-1 ELIS bundled panels (CBC, CMP,
    // LIPID, Cotinine). Always shown above the tests list so ops can
    // bind the OHD panel to one of them in a single click.
    const bundled = useMemo(
        () => assays.filter((a) => a.isBundledPanel).sort((a, b) => a.name.localeCompare(b.name)),
        [assays]
    );
    if (bundled.length === 0) return null;

    return (
        <div className="flex-shrink-0">
            <div className="border-b border-mld-panel-border bg-[#f8f6f1] px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-mld-muted-text">
                Map panel to bundled assay
            </div>
            <ul className="divide-y divide-[#efeae2]">
                <li>
                    <label
                        className={clsx(
                            'flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-xs transition',
                            selectedCode === null ? 'bg-mld-primary/10' : 'hover:bg-[#f4f1eb]'
                        )}>
                        <input
                            type="radio"
                            name="panel-assay"
                            checked={selectedCode === null}
                            disabled={disabled}
                            onChange={() => onChange(null)}
                            className="h-3.5 w-3.5 text-mld-primary focus:ring-mld-primary"
                        />
                        <span className="text-xs italic text-mld-muted-text">None — no completion count</span>
                    </label>
                </li>
                {bundled.map((a) => {
                    const isPicked = selectedCode === a.code;
                    return (
                        <li key={a.code}>
                            <label
                                className={clsx(
                                    'flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-xs transition',
                                    isPicked ? 'bg-mld-primary/10' : 'hover:bg-[#f4f1eb]'
                                )}>
                                <input
                                    type="radio"
                                    name="panel-assay"
                                    checked={isPicked}
                                    disabled={disabled}
                                    onChange={() => onChange(a.code)}
                                    className="h-3.5 w-3.5 text-mld-primary focus:ring-mld-primary"
                                />
                                <span className="flex-1 truncate font-semibold text-mld-menu-text">{a.name}</span>
                                <span className="rounded-full bg-[#fef3c7] px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-[#92400e]">
                                    Panel
                                </span>
                                <span className="font-mono text-[10px] text-mld-muted-text">{a.code}</span>
                            </label>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}

function IndividualAssayPicker({
    assays,
    assaySearch,
    setAssaySearch,
    selectedCode,
    disabled,
    onChange,
}: {
    assays: AssayOption[];
    assaySearch: string;
    setAssaySearch: (v: string) => void;
    selectedCode: string | null;
    disabled: boolean;
    onChange: (code: string | null) => void;
}) {
    // Individual assays only — bundled panels live in the left-side
    // BundledPanelRadios group so they don't appear twice. Both
    // pickers write to the same `assay_code` field; selecting an
    // individual here clears any bundled selection on the left, and
    // vice versa (the radios share a name attribute).
    const filtered = useMemo(() => {
        const individuals = assays.filter((a) => !a.isBundledPanel);
        const needle = assaySearch.trim().toLowerCase();
        if (!needle) return individuals;
        return individuals.filter(
            (a) => a.name.toLowerCase().includes(needle) || a.code.toLowerCase().includes(needle)
        );
    }, [assays, assaySearch]);

    return (
        <div className="flex h-full min-h-0 flex-col">
            <div className="border-b border-mld-panel-border px-3 py-2">
                <label className="relative block">
                    <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-mld-muted-text" />
                    <input
                        type="text"
                        value={assaySearch}
                        onChange={(e) => setAssaySearch(e.target.value)}
                        placeholder="Filter individual assays…"
                        className="w-full rounded-md border border-mld-panel-border bg-white py-1.5 pl-8 pr-3 text-xs text-mld-menu-text placeholder:text-mld-muted-text focus:border-mld-primary focus:outline-none focus:ring-1 focus:ring-mld-primary"
                    />
                </label>
            </div>

            <div className="max-h-[55vh] flex-1 overflow-y-auto">
                {filtered.length === 0 && (
                    <p className="px-3 py-6 text-[11px] text-mld-muted-text">No assays match.</p>
                )}
                <ul className="divide-y divide-[#efeae2]">
                    {filtered.map((a) => {
                        const isPicked = selectedCode === a.code;
                        return (
                            <li key={a.code}>
                                <label
                                    className={clsx(
                                        'flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-xs transition',
                                        isPicked ? 'bg-mld-primary/10' : 'hover:bg-[#f4f1eb]'
                                    )}>
                                    <input
                                        type="radio"
                                        name="panel-assay"
                                        checked={isPicked}
                                        disabled={disabled}
                                        onChange={() => onChange(a.code)}
                                        className="h-3.5 w-3.5 text-mld-primary focus:ring-mld-primary"
                                    />
                                    <span className="flex-1 truncate font-semibold text-mld-menu-text">{a.name}</span>
                                    {a.isCalculated && (
                                        <span className="rounded-full bg-[#dbeafe] px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-[#1e40af]">
                                            Calc
                                        </span>
                                    )}
                                    <span className="font-mono text-[10px] text-mld-muted-text">{a.code}</span>
                                </label>
                            </li>
                        );
                    })}
                </ul>
            </div>
        </div>
    );
}
